import * as React from 'react';
import { useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { ModalContext } from '@/state/ModalProvider';
import {
  AppBar,
  InputAdornment,
  Stack,
  TextField,
  Toolbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { TaxCalculation } from '@/api/tax-guide';
import { useForm, useWatch } from 'react-hook-form';
import { TaxGuideContext } from '@/state/TaxGuideProvider';
import theme from '@/themes/morganstanley';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 700,
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #fff',
  boxShadow: 24,
  p: 4,
  '@media (max-width: 600px)': {
    width: '80%',
    maxWidth: '90%',
    p: 2,
  },
};

interface BasicInputs {
  noOfShares: string;
  sharePriceOnVesting: string;
  sharePriceOnSale: string;
  sharePriceOnAward: string;
  salary: string;
}

type Inputs = BasicInputs | Record<keyof BasicInputs, string>;

export default function BasicModal() {
  const modalStatus = useContext(ModalContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    trigger,
  } = useForm<Inputs>();

  const { updateRequest } = useContext(TaxGuideContext);

  const watchedValues = useWatch({ control });

  useEffect(() => {
    Object.keys(watchedValues).forEach(key => {
      trigger(key as keyof Inputs);
    });
  }, [watchedValues, trigger]);

  const handleClose = () => {
    modalStatus.setModal(false);
  };
  const noOfRecords = modalStatus.modalValues.length;

  const textboxMappingFunction = (entry: TaxCalculation, index: number) => {
    let boxWidth;

    const commonValidationRuleObject = {
      required: {
        value: true,
        message: 'Input required',
      },
      validate: {
        positiveNumber: (value: string) =>
          /^\d+(\.\d+)?$/.test(value) || 'Input must be a positive number',
      },
      maxLength: {
        value: 9,
        message: 'Input must be upto 9 digits',
      },
      min: {
        value: 0,
        message: 'Input must be a positive number',
      },
    };
    let validationRules;
    switch (entry.id) {
      case 'noOfShares':
        validationRules = {
          ...commonValidationRuleObject,
          min: {
            value: 1,
            message: 'Input must be at least 1',
          },
          pattern: {
            value: /^\d+$/,
            message: 'Input must be a positive number',
          },
        };
        break;
      case 'salary':
        validationRules = {
          ...commonValidationRuleObject,
          maxLength: {
            value: 20,
            message: 'Input must be upto 20 digits',
          },
        };
        break;
      default:
        validationRules = { ...commonValidationRuleObject };
        break;
    }

    if (
      (noOfRecords % 2 == 0 &&
        (index == noOfRecords - 1 || index == noOfRecords - 2)) ||
      (noOfRecords % 2 == 1 && index == noOfRecords - 1)
    ) {
      boxWidth = '95%';
    } else {
      boxWidth = '45%';
    }

    return (
      <TextField
        label={entry.name}
        id={entry.id}
        key={index}
        sx={{
          p: 1.5,
          pl: 0,
          pr: 4,
          width: boxWidth,
          '@media (max-width: 600px)': {
            width: '90%',
            pr: 2,
            p: 1,
          },
        }}
        defaultValue={entry.value}
        variant="standard"
        aria-describedby={entry.name}
        aria-label={entry.name}
        {...register(entry.id, validationRules)}
        error={errors[entry.id] ? true : false}
        helperText={errors[entry.id] ? errors[entry.id]?.message : ' '}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                {entry.currencyCode}
              </InputAdornment>
            ),
          },
          inputLabel: {
            sx: {
              fontSize: theme.typography.sample_calculation_label.fontSize,
            },
          },
        }}
      />
    );
  };

  const onSubmit = (data: Inputs) => {
    const taxCalculationPopupObject = Object.entries(data).map(
      ([key, value]) => ({
        id: key as keyof BasicInputs,
        value: value.toString(),
      }),
    );
    updateRequest({
      taxCalculationPopupList: taxCalculationPopupObject,
    });
    modalStatus.setModal(false);
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      aria-labelledby="modal-for-sampleCalculations"
      aria-describedby="modal-for-testing-different-calculation-scenarios"
    >
      <Box sx={style}>
        <AppBar position="fixed">
          <Toolbar>
            <Typography id="modal-modal-title" variant="h6" component="h6">
              Calculation Example
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ mt: 6 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {modalStatus.modalValues.map((entry, index) =>
              textboxMappingFunction(entry, index),
            )}

            <Stack
              sx={{
                p: 2,
                pb: 0,
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
              }}
              direction={'row'}
              spacing={2}
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                endIcon={<CloseIcon />}
              >
                Close
              </Button>
              <Button variant="contained" endIcon={<SendIcon />} type="submit">
                Calculate
              </Button>
            </Stack>
          </form>
        </Box>
      </Box>
    </Modal>
  );
}
